/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type EntityMigrationDoneScreenProps = {
    /**
     * The old version name.
     */
    oldVersionName: string;

    /**
     * Indicator whether the migration failed.
     */
    isMigrationFailed: boolean;

    /**
     * Indicator whether the old version training failed.
     */
    isOldVersionTrainingFailed: boolean;

    /**
     * Indicator whether the new version training failed.
     */
    isNewVersionTrainingFailed: boolean;

    /**
     * The migration error message in case that the migration
     * failed.
     */
    migrationFailureReason?: string;
};

const messages = defineMessages({
    description: {
        id: 'entityMigration.entityMigrationDoneScreen.description',
        defaultMessage: 'Your composites were successfully upgraded!'
    },
    learnMoreTitle: {
        id: 'entityMigration.entityMigrationDoneScreen.learnMoreTitle',
        defaultMessage: 'Learn more about entities:'
    },
    improveEntitiesLinkText: {
        id: 'entityMigration.entityMigrationDoneScreen.improveEntitiesLinkText',
        defaultMessage: 'Improved machine-learned entities'
    },
    manageVersionsLinkText: {
        id: 'entityMigration.entityMigrationDoneScreen.manageVersionsLinkText',
        defaultMessage: 'How to manage your app with versions'
    },
    oldVersionTrainingFailedDescription: {
        id: 'entityMigration.entityMigrationDoneScreen.oldVersionTrainingFailedDescription',
        defaultMessage: `Your composites were successfully upgraded,but we were unable to train version
        "{ oldVersionName }". Your untrained changes in "{ oldVersionName }" remain untrained.`
    },
    newVersionTrainingFailedDescription: {
        id: 'entityMigration.entityMigrationDoneScreen.newVersionTrainingFailedDescription',
        defaultMessage: `Your composites were successfully upgraded, but we were unable
        to train your new version. Please try training your model again later.`
    },
    trainingFailedDescription: {
        id: 'entityMigration.entityMigrationDoneScreen.trainingFailedDescription',
        defaultMessage: `Your composites were successfully upgraded, but we were unable to train
        version "{ oldVersionName }" or your new version. Please try training your model again later.`
    },
    migrationFailedDescription: {
        id: 'entityMigration.entityMigrationDoneScreen.migrationFailedDescription',
        defaultMessage: 'Upgrading your composite entities has failed. Please try again later.'
    },
    migrationFailureErrorTitle: {
        id: 'entityMigration.entityMigrationDoneScreen.migrationFailureErrorTitle',
        defaultMessage: 'Error details:'
    }
});

const screenTheme = getScopedTheme('EntityMigrationDoneScreen');
const errorIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { color: screenTheme.errorIconColor, fontSize: '14px', lineHeight: '20px' }
});

/**
 * Displays the content of the done screen with different states
 * either migration and training are successfully done or any of
 * them failed.
 */
export const EntityMigrationDoneScreen = (props: EntityMigrationDoneScreenProps) => {
    const { isMigrationFailed, isOldVersionTrainingFailed, isNewVersionTrainingFailed, oldVersionName, migrationFailureReason } = props;
    const { localize } = useLocalization();

    let descriptionMessage = localize(messages.description);

    if (isOldVersionTrainingFailed && isNewVersionTrainingFailed) {
        descriptionMessage = localize(messages.trainingFailedDescription, { oldVersionName });
    } else if (isOldVersionTrainingFailed) {
        descriptionMessage = localize(messages.oldVersionTrainingFailedDescription, { oldVersionName });
    } else if (isNewVersionTrainingFailed) {
        descriptionMessage = localize(messages.newVersionTrainingFailedDescription);
    }

    if (isMigrationFailed) {
        descriptionMessage = localize(messages.migrationFailedDescription);
    }

    return (
        <Stack tokens={{ childrenGap: 60 }} styles={{ root: { minHeight: '354px' } }}>
            <LuisText data-automation-id="descriptionMessage">{descriptionMessage}</LuisText>

            {migrationFailureReason && (
                <Stack tokens={{ childrenGap: 6 }}>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <Icon iconName="StatusErrorFull" styles={errorIconStyles} />

                        <LuisText fontWeight="bold">{localize(messages.migrationFailureErrorTitle)}</LuisText>
                    </Stack>

                    <LuisText data-automation-id="migrationFailureReason" variant="smallPlus">
                        {migrationFailureReason}
                    </LuisText>
                </Stack>
            )}

            {!isMigrationFailed && (
                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText>{localize(messages.learnMoreTitle)}</LuisText>

                    <ExternalLink href="https://go.microsoft.com/fwlink/?linkid=2128339">
                        {localize(messages.improveEntitiesLinkText)}
                    </ExternalLink>

                    <ExternalLink href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-how-to-manage-versions">
                        {localize(messages.manageVersionsLinkText)}
                    </ExternalLink>
                </Stack>
            )}
        </Stack>
    );
};
